import {
  IExperienceMessageDenormalized,
  isContentBlock,
  isLayoutBlock
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { useMemo, useState } from 'react';
import { useUnsafeTranslation } from '../../../../i18next';
import { toMoment } from '../../../../services/time';
import { Content, LayoutBlockComponent } from './Task';

// This is very barebones for now (mimicking the old style used before the introduction of messges)
// but will move to something that resembles an inbox, // where you an see the subject, body and send date of a message.
//
// The most recent one will always be expanded, older ones are collapsed and can be expanded manually
// like in a regular mail program

const Heading = styled('div')<{ dense?: boolean }>((p) => ({
  marginBottom: p.theme.spacing(p.dense ? 0 : 2)
}));

const MessageDate = styled('div')((p) => ({
  marginTop: p.theme.spacing()
}));

type Variant = 'collapsed' | 'expanded' | 'alwaysExpanded';

const HIDE_MESSAGE_DATE = true;

const BlockContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const MessageContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const Message = ({
  d,
  variant = 'collapsed',
  size = 'normal'
}: {
  d: IExperienceMessageDenormalized;
  variant?: Variant;
  size?: 'small' | 'normal';
}) => {
  const { t } = useUnsafeTranslation();
  const [v, setV] = useState(variant);
  const headerSize = size === 'small' ? 'h5' : 'h3';
  return (
    <>
      <Heading dense={v === 'collapsed'}>
        <Typography variant={headerSize} component={headerSize}>
          {d.subject}
        </Typography>
        {!HIDE_MESSAGE_DATE && (
          <MessageDate>
            <Typography variant="caption" component="div">
              {t('Sent on')}{' '}
              <strong>{toMoment(d.sendAt).format('MMM DD, YYYY')}</strong>
            </Typography>
          </MessageDate>
        )}
      </Heading>
      {v !== 'collapsed' && (
        <>
          {(d.blocks || []).map((c) =>
            !isLayoutBlock(c) && c.parentLayoutElement ? null : (
              <BlockContainer key={c.id}>
                {isContentBlock(c) && <Content d={c} />}
                {isLayoutBlock(c) && (
                  <LayoutBlockComponent
                    taskId=""
                    b={c}
                    blocks={d.blocks}
                    highlight={false}
                  />
                )}
              </BlockContainer>
            )
          )}
        </>
      )}
      {v === 'collapsed' && (
        <Button
          color="inherit"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setV('expanded')}
        >
          {t('Show details')}
        </Button>
      )}
      {v === 'expanded' && (
        <Button
          color="inherit"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setV('collapsed')}
        >
          {t('Hide details')}
        </Button>
      )}
    </>
  );
};

export const MessageInboxCurrent = ({
  ds
}: {
  ds: IExperienceMessageDenormalized[];
}) => {
  const mostRecentMessage = ds[ds.length - 1];
  if (!mostRecentMessage) {
    return null;
  }
  return <Message d={mostRecentMessage} variant="alwaysExpanded" />;
};

const MessagesInboxPastHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

export const MessageInboxPast = ({
  ds
}: {
  ds: IExperienceMessageDenormalized[];
}) => {
  const { t } = useUnsafeTranslation();
  const pastMessages = useMemo(() => ds.slice(0, -1), [ds]);
  if (!pastMessages.length) {
    return null;
  }
  return (
    <>
      <MessagesInboxPastHeading>
        <Typography variant="h3" component="h3">
          {t('Past messages')}
        </Typography>
      </MessagesInboxPastHeading>
      {pastMessages.map((m) => (
        <MessageContainer key={m.templateId}>
          <Message d={m} variant="collapsed" size="small" />
        </MessageContainer>
      ))}
    </>
  );
};
